import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Headphones, ScrollText, Radio, CalendarClock, Inbox, Rss, ArrowUpRight,
  Share2, Plug, CalendarRange, Users, Cpu, Library, Mic, BarChart3,
  GraduationCap, CreditCard, LifeBuoy,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

const HUBS = {
  live: {
    testid: "live-on-air-hub-page",
    eyebrow: "// LIVE & ON-AIR",
    title: "You're on the air.",
    description: "Six live tools. Zero dead air. Pick your console.",
    footer: "// the red light is on",
    cardPrefix: "live-card",
    items: [
      { to: "/dashboard/ai-dj", label: "AI DJ Assistant", icon: Headphones, tint: "text-brand-amber bg-brand-amber/10 border-brand-amber/30",
        desc: "Your co-host that never sleeps — song intros, segues and banter in real time." },
      { to: "/dashboard/prompter", label: "The Prompter", icon: ScrollText, tint: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",
        desc: "Roll your script live on-air — with second-screen sync and proof-of-play." },
      { to: "/dashboard/automation", label: "Push to Air", icon: Radio, tint: "text-rose-300 bg-rose-500/10 border-rose-500/30",
        desc: "Send finished audio straight into your automation system." },
      { to: "/guide", label: "Program Guide", icon: CalendarClock, tint: "text-violet-300 bg-violet-500/10 border-violet-500/30",
        desc: "Your public show schedule — always current, always shareable." },
      { to: "/dashboard/request-line", label: "Request Line", icon: Inbox, tint: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
        desc: "Listener requests, voice shoutouts and the weekly leaderboard." },
      { to: "/dashboard/nowplaying", label: "Now Playing", icon: Rss, tint: "text-orange-300 bg-orange-500/10 border-orange-500/30",
        desc: "Live metadata feeds and now-playing cards for your stream." },
    ],
  },
  distribution: {
    testid: "distribution-hub-page",
    eyebrow: "// DISTRIBUTION",
    title: "Everywhere at once.",
    description: "Three channels out. One source of truth.",
    footer: "// press send once, land everywhere",
    cardPrefix: "distribution-card",
    items: [
      { to: "/dashboard/social-connections", label: "Social Connections", icon: Share2, tint: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",
        desc: "Connect Facebook, Instagram, X and more — auto-post everything you make." },
      { to: "/dashboard/integrations", label: "Integrations", icon: Plug, tint: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
        desc: "WordPress, metadata sources and third-party services in one panel." },
      { to: "/dashboard/calendar", label: "Schedule", icon: CalendarRange, tint: "text-violet-300 bg-violet-500/10 border-violet-500/30",
        desc: "Plan and queue posts across every platform from one calendar." },
    ],
  },
  stationops: {
    testid: "station-ops-hub-page",
    eyebrow: "// STATION OPERATIONS",
    title: "The engine room.",
    description: "Seven back-office tools that keep the station humming.",
    footer: "// smooth running, no static",
    cardPrefix: "stationops-card",
    items: [
      { to: "/dashboard/team", label: "Team", icon: Users, tint: "text-brand-amber bg-brand-amber/10 border-brand-amber/30",
        desc: "Invite producers and DJs — control who can touch what." },
      { to: "/dashboard/station", label: "Station", icon: Radio, tint: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",
        desc: "Station identity, branding and broadcast settings." },
      { to: "/dashboard/providers", label: "AI Providers", icon: Cpu, tint: "text-violet-300 bg-violet-500/10 border-violet-500/30",
        desc: "Bring your own keys and route AI workloads across providers." },
      { to: "/dashboard/library", label: "Content Library", icon: Library, tint: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
        desc: "Every asset you've generated, searchable in one vault." },
      { to: "/dashboard/transcribe", label: "Transcribe", icon: Mic, tint: "text-rose-300 bg-rose-500/10 border-rose-500/30",
        desc: "Turn any audio into clean, editable text." },
      { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3, tint: "text-sky-300 bg-sky-500/10 border-sky-500/30",
        desc: "Reach, output and engagement — the numbers that matter." },
      { to: "/dashboard/academy", label: "Academy", icon: GraduationCap, badge: "PRO", tint: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/30",
        desc: "Courses and lessons to level up your whole crew." },
    ],
  },
  account: {
    testid: "account-hub-page",
    eyebrow: "// ACCOUNT",
    title: "Your account.",
    description: "Money and help — both one click away.",
    footer: "// we've got your back",
    cardPrefix: "account-card",
    items: [
      { to: "/dashboard/billing", label: "Billing", icon: CreditCard, tint: "text-brand-amber bg-brand-amber/10 border-brand-amber/30",
        desc: "Plans, payment gateways, credits and usage in one place." },
      { to: "/dashboard/help", label: "Help & Support", icon: LifeBuoy, tint: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",
        desc: "Guides, tickets and live help when you need a hand." },
    ],
  },
};

export default function GroupHub({ kind }) {
  const t = useT();
  const hub = HUBS[kind];
  const [stats, setStats] = useState({});
  useEffect(() => {
    api.get("/hub/stats").then((r) => setStats(r.data || {})).catch(() => {});
  }, []);
  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid={hub.testid}>
      <PageHeader eyebrow={hub.eyebrow} title={hub.title} description={hub.description} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {hub.items.map((s, i) => (
          <Link
            key={s.to}
            to={s.to}
            data-testid={`${hub.cardPrefix}-${s.to.split("/").pop()}`}
            className="group relative rounded-xl border border-line bg-bg-surface p-5 transition hover:border-brand-amber/60 hover:bg-bg-elevated"
            style={{ animation: `fade-up 0.4s ease both`, animationDelay: `${i * 45}ms` }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`h-11 w-11 rounded-lg border flex items-center justify-center ${s.tint}`}>
                <s.icon className="h-5 w-5" />
              </div>
              <div className="flex items-center gap-2">
                {stats[s.to] && (
                  <span
                    data-testid={`hub-stat-${s.to.split("/").pop()}`}
                    className="text-[9px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                  >
                    {stats[s.to]}
                  </span>
                )}
                <ArrowUpRight className="h-4 w-4 text-ink-muted opacity-0 group-hover:opacity-100 group-hover:text-brand-amber transition" />
              </div>
            </div>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="font-display text-lg font-bold">{t(s.label)}</div>
              {s.badge && (
                <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-amber/15 text-brand-amber">
                  {s.badge}
                </span>
              )}
            </div>
            <div className="text-sm text-ink-secondary leading-relaxed">{t(s.desc)}</div>
          </Link>
        ))}
      </div>
      <div className="mt-8 text-[11px] font-mono uppercase tracking-[0.2em] text-ink-muted text-center">
        {hub.footer}
      </div>
    </div>
  );
}
